import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Copy } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Panel, PageHead, StatusBadge, Empty } from "@/components/common";
import { RejectDialog } from "@/components/RejectDialog";
import { useApi, errMsg, inr, fmtDate } from "@/lib/api";

export default function Withdrawals() {
  const api = useApi();
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const load = useCallback(() => api("get", `/admin/withdrawals${status === "all" ? "" : `?status=${status}`}`).then(setRows), [api, status]);
  useEffect(() => { load(); }, [load]);

  const act = async (path, body, msg) => {
    try { await api("post", path, body); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-withdrawals-page">
      <PageHead label="Admin" title="Withdrawals" />
      <Panel>
        <Tabs value={status} onValueChange={setStatus} className="mb-6">
          <TabsList>{["pending", "paid", "rejected", "all"].map((s) => <TabsTrigger key={s} value={s} data-testid={`withdrawals-tab-${s}`} className="capitalize">{s}</TabsTrigger>)}</TabsList>
        </Tabs>
        {!rows ? <p className="text-sm text-slate-500">Loading…</p> : rows.length === 0 ? <Empty testId="withdrawals-empty" text="Nothing here." /> : (
          <Table data-testid="withdrawals-table">
            <TableHeader><TableRow><TableHead>Member</TableHead><TableHead>UPI ID</TableHead><TableHead>Amount</TableHead><TableHead>Requested</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((w) => (
                <TableRow key={w.id} data-testid={`withdrawal-row-${w.id}`}>
                  <TableCell><p className="text-white">{w.name}</p><p className="text-xs text-slate-500">{w.email}</p></TableCell>
                  <TableCell>
                    <button className="inline-flex items-center gap-1 font-mono text-xs text-emerald-400" data-testid={`copy-withdraw-upi-${w.id}`} onClick={() => { navigator.clipboard.writeText(w.upi_id); toast.success("UPI ID copied"); }}>{w.upi_id}<Copy className="h-3 w-3" /></button>
                  </TableCell>
                  <TableCell className="font-mono text-white">{inr(w.amount)}</TableCell>
                  <TableCell className="text-xs">{fmtDate(w.created_at)}</TableCell>
                  <TableCell><StatusBadge status={w.status} /></TableCell>
                  <TableCell className="text-right">
                    {w.status === "pending" && (
                      <div className="flex justify-end gap-2">
                        <Button size="sm" data-testid={`mark-paid-btn-${w.id}`} onClick={() => act(`/admin/withdrawals/${w.id}/paid`, {}, "Marked as paid")}><Check className="mr-1 h-4 w-4" />Mark paid</Button>
                        <Button size="sm" variant="outline" data-testid={`reject-withdraw-btn-${w.id}`} onClick={() => setRejecting(w)}><X className="mr-1 h-4 w-4" />Reject</Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>
      <RejectDialog open={!!rejecting} title="Reject & refund to wallet" onClose={() => setRejecting(null)}
        onConfirm={(reason) => { act(`/admin/withdrawals/${rejecting.id}/reject`, { reason }, "Rejected and refunded"); setRejecting(null); }} />
    </div>
  );
}
